import { useMemo } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  useSavedSearches,
  useDeleteSavedSearch,
} from '@/hooks/queries'
import { useAuth } from '@/contexts/AuthContext'
import type { SavedSearch } from '@/lib/types'

/**
 * Settings — Saved filters.
 *
 * Wave-1 P5 settings primitive UI. The "Save filter" affordance itself
 * lives on each list page (next to the filter rail) and posts to
 * `POST /preferences/:userId/searches` with `name = <page-slug>`. This
 * Settings section LISTS what the user has saved so they can review and
 * delete entries when the saved defaults stop matching their workflow.
 *
 * Filter keys are page slugs, conventionally one of: `stories`,
 * `vulnerabilities`, `threat-actors`, `benchmarks`, `regulatory`,
 * `regulatory-actions`. The Settings section displays each entry with its
 * filter-key name and a compact chip list of the saved filter values, so
 * the user can decide whether the saved state still matches what they want
 * the page to default to.
 *
 * Captain Input #4 (locked, 2026-05-02 spec): if a user has no saved
 * filters, the affected pages render the global view. We do NOT prompt
 * for migration or synthesise defaults.
 */
export function SavedFiltersSection() {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const { data: savedSearches = [], isLoading } = useSavedSearches(userId)
  const deleteSavedSearch = useDeleteSavedSearch()

  // Group + sort by createdAt desc so the most recent save per filter-key
  // is shown first. The hook (useFilterDefaultsFromPrefs) applies the same
  // last-write-wins rule when reading.
  const sorted = useMemo(
    () =>
      [...savedSearches].sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      ),
    [savedSearches],
  )

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium">Saved filters</p>
        <p className="text-xs text-muted-foreground">
          Saved from a list page&apos;s &ldquo;Save filter&rdquo; control.
          Each entry seeds the matching list page&apos;s filter rail on
          subsequent loads.
        </p>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground italic">Loading…</p>
      ) : sorted.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No saved filters yet. Use the &ldquo;Save filter&rdquo; button on
          a list page to create one.
        </p>
      ) : (
        <ul
          className="flex flex-col gap-2"
          aria-label="Saved filter list"
          data-testid="saved-filters-list"
        >
          {sorted.map((ss) => (
            <SavedFilterRow
              key={ss.id}
              search={ss}
              onDelete={() =>
                deleteSavedSearch.mutate({ userId, id: ss.id })
              }
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function SavedFilterRow({
  search,
  onDelete,
}: {
  search: SavedSearch
  onDelete: () => void
}) {
  const summary = useMemo(() => summariseFilters(search.filters), [search.filters])
  return (
    <li
      className="flex items-start justify-between gap-3 rounded-md border bg-card px-3 py-2"
      data-testid={`saved-filter-${search.name}`}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-sm font-medium">{search.name}</span>
        {summary.length === 0 ? (
          <span className="text-xs italic text-muted-foreground">
            No filter values saved.
          </span>
        ) : (
          <div
            className="flex flex-wrap gap-1.5"
            role="list"
            // R28: the user's own saved-filter name, in an attribute.
            data-ph-no-capture
            aria-label={`Saved filters for ${search.name}`}
          >
            {summary.map((s) => (
              <span
                key={s}
                role="listitem"
                className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
              >
                {s}
              </span>
            ))}
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 text-destructive"
        onClick={onDelete}
        // R28: the user's own saved-filter name, in an attribute.
        data-ph-no-capture
        aria-label={`Delete saved filter ${search.name}`}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </li>
  )
}

/**
 * Renders a saved-search `filters` payload as a compact `key: value` chip list.
 * Arrays render as `key: a, b, c`; primitives stringify directly. Nested
 * objects collapse to `[object]` rather than spamming the row.
 */
function summariseFilters(filters: Record<string, unknown>): string[] {
  if (!filters || typeof filters !== 'object') return []
  const out: string[] = []
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v)) {
      if (v.length === 0) continue
      out.push(`${k}: ${v.join(', ')}`)
    } else if (typeof v === 'object') {
      out.push(`${k}: [object]`)
    } else {
      out.push(`${k}: ${String(v)}`)
    }
  }
  return out
}
